import { View } from 'react-native';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useThemeColor } from '@/hooks/use-theme-color';
import { FullLogoIcon } from './logos/full_logo';
import { LogoText } from './logos/logo';
import { IconLogo } from './logos/icon_logo';

type LogoVariant = 'full' | 'text' | 'icon';

type LogoColor = 'primary' | 'foreground' | 'tint' | 'white' | 'black' | string;

type LogoProps = {
  variant?: LogoVariant;
  size?: number;
  width?: number | string;
  height?: number | string;
  color?: LogoColor;
  lightColor?: string;
  darkColor?: string;
  hasMargin?: boolean;
  className?: string;
  showIcon?: boolean;
  showText?: boolean;
};

export const Logo = ({
  variant = 'full',
  size,
  width,
  height,
  color,
  lightColor,
  darkColor,
  hasMargin = false,
  className,
  showIcon,
  showText,
}: LogoProps) => {
  const theme = useColorScheme() ?? 'light';
  const containerMarginBottom = hasMargin ? (size || 52) * 0.9 : 0;

  const primaryColor = useThemeColor({}, 'tint');
  const tintColor = useThemeColor({}, 'tint');

  const getColor = (): string => {
    if (lightColor && darkColor) {
      return theme === 'dark' ? darkColor : lightColor;
    }

    if (typeof color === 'string') {
      switch (color) {
        case 'primary':
          return primaryColor;
        case 'foreground':
          return theme === 'dark' ? '#FFFFFF' : '#000000';
        case 'tint':
          return tintColor;
        case 'white':
          return '#FFFFFF';
        case 'black':
          return '#000000';
        default:
          return color;
      }
    }

    return theme === 'dark' ? '#FFFFFF' : '#000000';
  };

  const logoColor = getColor();

  const getDimensions = () => {
    if (width && height) {
      return { width, height };
    }
    if (size) {
      let aspectRatio;

      if (variant === 'full') {
        // Se variant é "full", verifica showIcon/showText para determinar aspectRatio
        const actualShowIcon = showIcon !== undefined ? showIcon : true;
        const actualShowText = showText !== undefined ? showText : true;

        if (actualShowIcon && !actualShowText) {
          // Só ícone
          aspectRatio = 469.46 / 458.92;
        } else if (!actualShowIcon && actualShowText) {
          // Só texto
          aspectRatio = 2535.68 / 453.89;
        } else {
          // Ambos (full)
          aspectRatio = 2901.56 / 458.92;
        }
      } else {
        aspectRatio = variant === 'icon' ? 469.46 / 522.97 : 2535.68 / 453.89;
      }

      return {
        width: size * 4,
        height: (size * 4) / aspectRatio,
      };
    }
    return {
      width: width || 208,
      height: height || (variant === 'full' ? 33 : variant === 'icon' ? 44 : 36),
    };
  };

  const dimensions = getDimensions();

  return (
    <View className={`flex-row ${className || ''}`} style={{ marginBottom: containerMarginBottom }}>
      {variant === 'full' ? (
        <FullLogoIcon
          width={dimensions.width}
          height={dimensions.height}
          color={logoColor}
          showIcon={showIcon !== undefined ? showIcon : true}
          showText={showText !== undefined ? showText : true}
        />
      ) : variant === 'icon' ? (
        <IconLogo width={dimensions.width} height={dimensions.height} color={logoColor} />
      ) : (
        <LogoText width={dimensions.width} height={dimensions.height} color={logoColor} />
      )}
    </View>
  );
};
